import { useEffect, useMemo, useState } from "react";
import JSZip from "jszip";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { ChevronLeft, ChevronRight, Download, FileArchive, Loader2 } from "lucide-react";
import { STORY_STYLES, renderSlide, canvasToBlob, downloadBlob } from "../lib/storyCanvas";
import { TelegramButton } from "./TelegramButton";

export function parseStories(text) {
  const re = /\*{0,2}\s*Slide\s*(\d+)\s*[:\-–—]?\s*\*{0,2}\s*[:\-–—]?/gi;
  const marks = [...text.matchAll(re)];
  const stories = [];
  marks.forEach((m, i) => {
    const end = i + 1 < marks.length ? marks[i + 1].index : text.length;
    const seg = text.slice(m.index + m[0].length, end).replace(/\*\*/g, "");
    const body = (seg.split(/\n\s*\n/).map((p) => p.replace(/^[-–—\s]+$/gm, "").trim()).find(Boolean) || "");
    if (Number(m[1]) === 1 || stories.length === 0) stories.push({ slides: [] });
    if (body) stories[stories.length - 1].slides.push(body);
  });
  return stories.filter((s) => s.slides.length);
}

const pill = "flex items-center gap-1.5 text-xs px-3 py-2 rounded-full font-semibold transition-colors disabled:opacity-50";

export function PhoneMockup({ open, onOpenChange, slides = [], imageUrl, imageId = "", color = "#10B981", onDownload, testid = "phone-mockup" }) {
  const [i, setI] = useState(0);
  const [style, setStyle] = useState("classic");
  const [renders, setRenders] = useState([]);
  const [busy, setBusy] = useState(false);
  const hasText = slides.length > 0;
  const total = Math.max(1, slides.length);
  const key = useMemo(() => slides.join("|"), [slides]);
  useEffect(() => { setI(0); }, [open, key]);

  useEffect(() => {
    if (!open || !hasText) { setRenders([]); return; }
    let alive = true;
    Promise.all(slides.map((t) => renderSlide({ imageUrl, text: t, style, color })))
      .then((cs) => alive && setRenders(cs))
      .catch(() => toast.error("Impossibile comporre le slide"));
    return () => { alive = false; };
  }, [open, key, style, imageUrl, color, hasText]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (d) => setI((p) => Math.min(total - 1, Math.max(0, p + d)));
  const blobs = () => Promise.all(renders.map(canvasToBlob));
  const downloadOne = async () => downloadBlob(await canvasToBlob(renders[i]), `story-slide-${i + 1}.png`);
  const downloadZip = async () => {
    setBusy(true);
    try {
      const zip = new JSZip();
      (await blobs()).forEach((b, k) => zip.file(`story-slide-${k + 1}.png`, b));
      downloadBlob(await zip.generateAsync({ type: "blob" }), "story-slides.zip");
    } finally { setBusy(false); }
  };
  const current = renders[i];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-transparent border-0 shadow-none max-w-[380px] p-0" data-testid={testid}>
        <DialogTitle className="sr-only">Anteprima Story</DialogTitle>
        <DialogDescription className="sr-only">Anteprima in cornice smartphone</DialogDescription>
        {hasText && (
          <div className="flex justify-center gap-1.5 mb-2" data-testid={`${testid}-styles`}>
            {STORY_STYLES.map((st) => (
              <button key={st.id} onClick={() => setStyle(st.id)} data-testid={`${testid}-style-${st.id}`}
                className="text-[11px] px-3 py-1.5 rounded-full border transition-colors"
                style={{ borderColor: style === st.id ? color : "rgba(255,255,255,0.15)", background: style === st.id ? `${color}33` : "rgba(0,0,0,0.5)", color: "#fff" }}>
                {st.label}
              </button>
            ))}
          </div>
        )}
        <div className="mx-auto w-[300px] rounded-[2.8rem] p-3 bg-[#0b0c10] border border-white/15 shadow-2xl">
          <div className="relative overflow-hidden rounded-[2.2rem] bg-black" style={{ aspectRatio: "9 / 16" }}>
            {hasText ? (
              current ? <img src={current.toDataURL("image/png")} alt="Slide" className="absolute inset-0 h-full w-full" data-testid={`${testid}-render`} />
                : <div className="absolute inset-0 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-white/60" /></div>
            ) : imageUrl ? (
              <img src={imageUrl} alt="Story" className="absolute inset-0 h-full w-full object-cover" data-testid={`${testid}-image`} />
            ) : null}
            {hasText && <span className="sr-only" data-testid={`${testid}-slide-text`}>{slides[i]}</span>}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black z-20" />
            <div className="absolute top-9 left-3 right-3 flex gap-1 z-20">
              {Array.from({ length: total }).map((_, k) => (
                <div key={k} className="h-[3px] flex-1 rounded-full bg-white/30 overflow-hidden">
                  <div className="h-full bg-white transition-[width] duration-300" style={{ width: k <= i ? "100%" : "0%" }} />
                </div>
              ))}
            </div>
            <button aria-label="Slide precedente" data-testid={`${testid}-prev`} onClick={() => go(-1)} className="absolute inset-y-0 left-0 w-1/3 z-20" />
            <button aria-label="Slide successiva" data-testid={`${testid}-next`} onClick={() => go(1)} className="absolute inset-y-0 right-0 w-1/3 z-20" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 mt-3">
          <button onClick={() => go(-1)} disabled={i === 0} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-xs text-slate-300" data-testid={`${testid}-counter`}>{i + 1} / {total}</span>
          <button onClick={() => go(1)} disabled={i >= total - 1} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
          {hasText ? (
            <>
              <button data-testid={`${testid}-download-slide`} onClick={downloadOne} disabled={!current} className={`${pill} bg-white text-slate-900`}>
                <Download className="h-3.5 w-3.5" /> Scarica slide
              </button>
              <button data-testid={`${testid}-download-zip`} onClick={downloadZip} disabled={busy || renders.length !== slides.length} className={`${pill} bg-white/15 text-white hover:bg-white/25`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileArchive className="h-3.5 w-3.5" />} ZIP ({slides.length})
              </button>
              <TelegramButton testid={`${testid}-telegram`} className={`${pill} bg-[#229ED9] text-white`} label="Pubblica su Telegram"
                getPayload={async () => ({ blobs: await blobs() })} />
            </>
          ) : (
            <>
              {onDownload && (
                <button data-testid={`${testid}-download`} onClick={onDownload} className={`${pill} bg-white text-slate-900`}>
                  <Download className="h-3.5 w-3.5" /> Scarica
                </button>
              )}
              {imageId && (
                <TelegramButton testid={`${testid}-telegram`} className={`${pill} bg-[#229ED9] text-white`} label="Pubblica su Telegram"
                  getPayload={async () => ({ imageId })} />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
